import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import type { UserPermission } from '@adornis/users/db/types.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { InsertOneResult } from 'mongodb';
import { mmr } from '../../mails/mail-registry.js';
import type { MatchingPermission } from '../permissions.js';
import { MatchingLanguage } from './auto-matching-fields.js';

/**
 * @entity BuddyUser
 */
@Entity()
export abstract class BuddyUser extends AdornisUser<UserPermission | MatchingPermission> {
  static override _class = 'BuddyUser';

  constructor(doc: PartialEntityData<BuddyUser>) {
    super(doc);
  }

  @validate(nonOptional())
  @Field(type => ID)
  timeframeID: Maybe<string>;

  @Field(type => String)
  note: Maybe<string>;

  static override async afterCreate(user: BuddyUserMentor, result: InsertOneResult) {
    await super.afterCreate(user, result);

    void this._sendRegistrationMail(user);
  }

  private static async _sendRegistrationMail(user: BuddyUser) {
    try {
      const recipients = [new Recipient(user.email, undefined, undefined)];
      const mailOptions = { recipients, mailer: await mailer };

      if (user instanceof BuddyUserMentor) return await mmr.registrationMentor(user._id).send(mailOptions);
      if (user instanceof BuddyUserMentee) return await mmr.registrationMentee(user._id).send(mailOptions);

      throw new Error('user instance unknown ' + user._class);
    } catch (err) {
      logger.error({ err, userID: user._id }, 'Couldnt send registration mail to user');
    }
  }

  static getMailVariables(user: BuddyUser): [string, string][] {
    return [];
  }
}

/**
 * @entity BuddyUserMentor
 */
@Entity()
export class BuddyUserMentor extends BuddyUser {
  static override _class = 'BuddyUserMentor';

  @DataLoadedField(type => [Match], ({ _id }) => ({ mentorID: _id }))
  matches: Maybe<Match[]>;
}

/**
 * @entity BuddyUserMentee
 */
@Entity()
export class BuddyUserMentee extends BuddyUser {
  static override _class = 'BuddyUserMentee';

  @DataLoadedField(type => [Match], ({ _id }) => ({ menteeID: _id }))
  matches: Maybe<Match[]>;
}

export enum GenderPreference {
  NO_PREFERENCE = 'NO_PREFERENCE',
  SAME_SEX = 'SAME_SEX',
}

registerEnum({ enum: GenderPreference, name: 'GenderPreference' });

export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  DIVERSE = 'DIVERSE',
  NOT_SPECIFIED = 'NOT_SPECIFIED',
}

registerEnum({ enum: Gender, name: 'Gender' });

registerEnum({ enum: MatchingLanguage, name: 'MatchingLanguage' });

/**
 * @entity DefaultMentorUser
 */
@Entity()
export class DefaultMentorUser extends BuddyUserMentor {
  static override _class = 'DefaultMentorUser';

  constructor(doc: PartialEntityData<DefaultMentorUser>) {
    super(doc);
  }

  @validate(nonOptional())
  @Field(type => Gender)
  gender: Maybe<Gender>;

  @validate(nonOptional())
  @Field(type => GenderPreference)
  genderPreference: Maybe<GenderPreference>;

  @Field(type => [MatchingLanguage])
  foreignLanguages: Maybe<MatchingLanguage[]>;

  @Field(type => MatchingLanguage)
  nativeLanguage: Maybe<MatchingLanguage>;
}

/**
 * @entity DefaultMenteeUser
 */
@Entity()
export class DefaultMenteeUser extends BuddyUserMentee {
  static override _class = 'DefaultMenteeUser';

  constructor(doc: PartialEntityData<DefaultMenteeUser>) {
    super(doc);
  }

  @Field(type => Gender)
  gender: Maybe<Gender>;

  @validate(nonOptional())
  @Field(type => GenderPreference)
  genderPreference: Maybe<GenderPreference>;

  @Field(type => [MatchingLanguage])
  foreignLanguages: Maybe<MatchingLanguage[]>;

  @Field(type => MatchingLanguage)
  nativeLanguage: Maybe<MatchingLanguage>;
}
